import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import type {
    CheckoutData,
    FinancialDataResponse,
    FinancialDetailsRequestV2,
    OfferValidation,
    PaymentData,
    PaymentPlan,
    PersonalDetailsClientDTO,
    PreQualifiedInfoDTO,
    ValidateVendorResponse,
} from '@leanpay/models'
import { MaturityType } from '@leanpay/models'

interface AppState {
    vendorToken: string
    isAuthenticated: boolean
    progress: number
    personalDetailsData: PersonalDetailsClientDTO | null
    financialDetailsDataV2: FinancialDetailsRequestV2 | null
    offerValidationData: OfferValidation | null
    selectedPaymentPlan: PaymentPlan | null
    paymentData: PaymentData | null
    checkoutData: CheckoutData | null
    vendor: string
    productCode: string
    vendorData: PreQualifiedInfoDTO | null
    validateVendorData: ValidateVendorResponse | null
    financialData: FinancialDataResponse | null
    maturityType: MaturityType
    lastRecordedRoute: string

    setVendorToken: (value: string) => void
    setIsAuthenticated: (value: boolean) => void
    setProgress: (value: number) => void
    setPersonalDetailsData: (value: PersonalDetailsClientDTO | null) => void
    setFinancialDetailsDataV2: (value: FinancialDetailsRequestV2 | null) => void
    setOfferValidationData: (value: OfferValidation | null) => void
    setSelectedPaymentPlan: (value: PaymentPlan | null) => void
    setPaymentData: (value: PaymentData | null) => void
    setCheckoutData: (value: CheckoutData | null) => void
    setVendorData: (value: PreQualifiedInfoDTO | null) => void
    setValidateVendorData: (value: ValidateVendorResponse | null) => void
    setFinancialData: (value: FinancialDataResponse | null) => void
    setMaturityType: (value: MaturityType) => void
    setVendor: (value: string) => void
    setProductCode: (value: string) => void
    setLastRecordedRoute: (route: string) => void
    canAccessRoute: (route: string) => boolean
    resetStore: () => void
}

const initialState = {
    vendorToken: '',
    isAuthenticated: false,
    progress: 0,
    personalDetailsData: null,
    financialDetailsDataV2: null,
    offerValidationData: null,
    selectedPaymentPlan: null,
    paymentData: null,
    checkoutData: null,
    vendor: '',
    productCode: '',
    vendorData: null,
    validateVendorData: null,
    financialData: null,
    maturityType: MaturityType.PRICE,
    lastRecordedRoute: '/',
}

export const useAppStore = create<AppState>()(
    persist(
        (set, get) => ({
            ...initialState,

            setVendorToken: (value) => set({ vendorToken: value }),
            setIsAuthenticated: (value) => set({ isAuthenticated: value }),
            setProgress: (value) => set({ progress: value }),
            setPersonalDetailsData: (value) => set({ personalDetailsData: value }),
            setFinancialDetailsDataV2: (value) => set({ financialDetailsDataV2: value }),
            setOfferValidationData: (value) => set({ offerValidationData: value }),
            setSelectedPaymentPlan: (value) => set({ selectedPaymentPlan: value }),
            setPaymentData: (value) => set({ paymentData: value }),
            setCheckoutData: (value) => set({ checkoutData: value }),
            setVendorData: (value) => set({ vendorData: value }),
            setValidateVendorData: (value) => set({ validateVendorData: value }),
            setFinancialData: (value) => set({ financialData: value }),
            setMaturityType: (value) => set({ maturityType: value }),
            setVendor: (value) => set({ vendor: value }),
            setProductCode: (value) => set({ productCode: value }),
            setLastRecordedRoute: (route) => set({ lastRecordedRoute: route }),
            canAccessRoute: (route) => {
                const { lastRecordedRoute } = get()
                return route === lastRecordedRoute
            },
            resetStore: () => set(initialState),
        }),
        {
            name: 'leanpay-self-checkout-session',
            storage: createJSONStorage(() => sessionStorage),
        },
    ),
)
